/**
 * Auto-links for the ACCESSORIES over a sink installation — the mirror, the
 * sconces flanking it, the overhead bar and the ceiling light above it.
 *
 * Extracted from `emitVanityLinks` (rules 1, 2, 3, 4 and 6) so the RUN's
 * emitter can share them rather than grow its own copy. It has to be shared
 * rather than picked: `mergeLinks` fully REGENERATES auto-links on every
 * build pass — every stored `source:'auto'` link is dropped and replaced by
 * the emitted set — so whichever emitter runs last owns the group's whole
 * record. A vanity assembled by `build_vanity` and then given a run would
 * have lost its sconce pair and its double-mirror `copy` on the first bay
 * edit, silently, because the run's emitter did not know those rules.
 *
 * What stays with each caller is what differs: `emitVanityLinks` also
 * records the double-sink pair and the centreline spine of a vanity ASSEMBLY;
 * `emitKitchenSinkLinks` records the basin → faucet `rp` that carries a
 * faucet's setback. The accessories above them are the same geometry either
 * way.
 *
 * Pure — no I/O, no mutation.
 */

const {
  computeRpOffset,
  computeRpRotYDiff,
  createLink,
  buildAnchorLocal,
  nearestByDx,
} = require('../group-customization');
const {
  getFixtureGroupMirrors,
  getFixtureGroupCeilingLights,
  getFixtureGroupOverheadLights,
  isSconce,
} = require('./fixtureGroup');

/** An rp link with `source: 'auto'`, offset in the anchor-local frame. */
function makeRpLink(sourceLocal, targetLocal) {
  if (!sourceLocal || !targetLocal) return null;
  return createLink('rp', {
    source: 'auto',
    sourceId: sourceLocal.id,
    targetId: targetLocal.id,
    offset: computeRpOffset(sourceLocal, targetLocal, 'copy'),
    rotYDiff: computeRpRotYDiff(sourceLocal, targetLocal),
    rotationMode: 'copy',
  });
}

/**
 * @param {Object}   options
 * @param {Object[]} options.groupObjects — one fixture group's world-space
 *   objects.
 * @param {Object}   options.anchor  — the group's anchor; every offset is
 *   computed in its local frame.
 * @param {Object[]} [options.mirrorSources] — what a mirror hangs over, in
 *   preference order (the faucets, else the basins). The nearest by
 *   anchor-local dx wins; with none, the anchor itself is the source.
 * @returns {{links: Object[], rpTargetIds: Set<string>}} `rpTargetIds` are the
 *   objects these rules now drive, so a caller adding a rail can keep the two
 *   constraint sets disjoint (a pair holding both disagrees by more than the
 *   solver's epsilon and makes the group undraggable).
 */
function emitAccessoryLinks({ groupObjects, anchor, mirrorSources = [] }) {
  const empty = { links: [], rpTargetIds: new Set() };
  if (!Array.isArray(groupObjects) || !anchor?.id) return empty;

  const groupId = anchor.fixtureGroupId ?? null;
  const mirrors = getFixtureGroupMirrors(groupObjects, groupId);
  const overheads = getFixtureGroupOverheadLights(groupObjects, groupId);
  const ceilingLights = getFixtureGroupCeilingLights(groupObjects, groupId);
  const sconces = groupObjects.filter(
    (o) => o?.fixtureGroupId === groupId && isSconce(o)
  );
  if (
    !mirrors.length &&
    !overheads.length &&
    !ceilingLights.length &&
    !sconces.length
  ) {
    return empty;
  }

  const localById = new Map();
  for (const o of [
    anchor,
    ...mirrorSources,
    ...mirrors,
    ...overheads,
    ...ceilingLights,
    ...sconces,
  ]) {
    if (!o?.id || localById.has(o.id)) continue;
    const local = buildAnchorLocal(o, anchor);
    if (local) localById.set(o.id, local);
  }
  const localsOf = (list) =>
    list.map((o) => localById.get(o.id)).filter(Boolean);

  const links = [];

  // 1) Mirror parents — whatever the mirror hangs over (preferred), else the
  //    anchor. Centring on the BASIN's side of the installation rather than
  //    the cabinet matters because a single basin is not always centred in
  //    its cabinet (`planVanityMirrors` says why).
  const sourceLocals = localsOf(mirrorSources);
  const mirrorLocals = localsOf(mirrors);
  for (const mirrorLocal of mirrorLocals) {
    const sourceLocal =
      nearestByDx(mirrorLocal, sourceLocals) || localById.get(anchor.id);
    if (!sourceLocal || sourceLocal.id === mirrorLocal.id) continue;
    const link = makeRpLink(sourceLocal, mirrorLocal);
    if (link) links.push(link);
  }

  // 2) Overhead and ceiling lights → the nearest mirror. A light exists to
  //    light a mirror and takes its height from the mirror's top, so a light
  //    with no mirror is left unlinked rather than tied to something it was
  //    never placed against.
  for (const lightLocal of localsOf([...overheads, ...ceilingLights])) {
    const sourceLocal = nearestByDx(lightLocal, mirrorLocals);
    if (!sourceLocal) continue;
    const link = makeRpLink(sourceLocal, lightLocal);
    if (link) links.push(link);
  }

  // 3) Sconces — grouped by nearest mirror; a PAIR becomes a centerLine
  //    around it, a lone sconce an rp from it. Three or more around one
  //    mirror is uncommon and ambiguous, and is skipped rather than recorded
  //    as a relationship nobody meant.
  const sconcesByMirrorId = new Map();
  for (const sconceLocal of localsOf(sconces)) {
    const nearestMirror = nearestByDx(sconceLocal, mirrorLocals);
    if (!nearestMirror) continue;
    if (!sconcesByMirrorId.has(nearestMirror.id)) {
      sconcesByMirrorId.set(nearestMirror.id, []);
    }
    sconcesByMirrorId.get(nearestMirror.id).push(sconceLocal);
  }
  for (const mirrorLocal of mirrorLocals) {
    const group = sconcesByMirrorId.get(mirrorLocal.id) || [];
    if (group.length === 0) continue;
    if (group.length === 1) {
      const link = makeRpLink(mirrorLocal, group[0]);
      if (link) links.push(link);
      continue;
    }
    if (group.length === 2) {
      const [a, b] = [...group].sort((x, y) => x.dx - y.dx);
      const link = createLink('centerLine', {
        source: 'auto',
        objectAId: a.id,
        objectBId: b.id,
        lineX: mirrorLocal.dx,
        rotationMode: 'mirror',
      });
      if (link) links.push(link);
    }
  }

  // 4) Two mirrors (a double vanity) keep their own children in step.
  if (mirrorLocals.length === 2) {
    const [a, b] = [...mirrorLocals].sort((x, y) => x.dx - y.dx);
    const link = createLink('copy', {
      source: 'auto',
      parentAId: a.id,
      parentBId: b.id,
    });
    if (link) links.push(link);
  }

  return {
    links: links.filter(Boolean),
    rpTargetIds: new Set(
      links.filter((l) => l?.type === 'rp').map((l) => l.targetId)
    ),
  };
}

module.exports = { emitAccessoryLinks };
